// apps/roleforge-app/src/components/respond/RespondConverseNotice.tsx
//
// Issue #1250 — distinct rendering for the stakeholder converse error codes
// introduced by the #1202 authorization rebuild. Before this, RespondShell
// swallowed every `{type:'error', code}` SSE frame into a generic failure, so
// a stakeholder whose 30-day invite link had aged out saw nothing actionable
// and had no idea the fix was "ask the owner to re-send".
//
// The codes come from `apps/server/src/services/roleforge-stakeholder-authz.ts`
// (`deny(...)`) and surface through the converse SSE stream:
//   invitation_expired → the link's TTL elapsed; owner must resend
//   invitation_revoked → the owner withdrew the invitation
//   thread_closed      → the thread is completed/revoked; no more replies
//   forbidden          → the grant does not cover this thread
// `credit_spend_*` codes are NOT handled here — those already have their own
// HostedCreditApprovalNotice affordance.

export type RespondConverseErrorCode =
  | 'invitation_expired'
  | 'invitation_revoked'
  | 'thread_closed'
  | 'forbidden'
  | (string & {});

export interface RespondConverseNoticeCopy {
  title: string;
  body: string;
  /** True when the interview cannot continue and the composer should close. */
  terminal: boolean;
}

export function respondConverseErrorCopy(
  code: string | undefined,
): RespondConverseNoticeCopy {
  switch (code) {
    case 'invitation_expired':
      return {
        title: 'This invitation link has expired',
        body:
          'Invitation links stay valid for a limited time. Ask the person who invited you to send a fresh one — your answers so far are saved.',
        terminal: true,
      };
    // PR #1372 finding N2: revocation now ends an in-flight session, not just
    // future token exchanges. Without its own case this landed on the default
    // "try sending it again" copy — a non-terminal message that leaves the
    // composer open and invites the stakeholder to retry something that can
    // never succeed.
    case 'invitation_revoked':
      return {
        title: 'This invitation was withdrawn',
        body:
          'The person who invited you has withdrawn this interview, so it is no longer taking replies. Your answers so far are saved. If you think this is a mistake, let them know.',
        terminal: true,
      };
    case 'thread_closed':
      return {
        title: 'This interview is closed',
        body:
          'This conversation has been marked complete, so it is no longer taking replies. Thank you — nothing further is needed from you.',
        terminal: true,
      };
    case 'forbidden':
      return {
        title: "You can't reply on this interview",
        body:
          'This link does not grant access to this conversation. Ask the person who invited you to send you your own link.',
        terminal: true,
      };
    default:
      return {
        title: 'That message did not go through',
        body:
          'Something went wrong on our side. Try sending it again — if it keeps failing, let the person who invited you know.',
        terminal: false,
      };
  }
}

interface Props {
  code: string | undefined;
  className?: string;
}

export function RespondConverseNotice({ code, className }: Props) {
  const copy = respondConverseErrorCopy(code);
  return (
    <div
      role="status"
      data-testid="respond-converse-notice"
      data-error-code={code ?? 'unknown'}
      data-terminal={copy.terminal ? 'true' : 'false'}
      className={`rounded-lg border border-line bg-paper px-4 py-3 text-sm text-ink ${className ?? ''}`}
    >
      <p className="font-medium">{copy.title}</p>
      <p className="mt-1 text-graphite">{copy.body}</p>
    </div>
  );
}
